import * as React from "react";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import {
  Stack,
  CardHeader,
  Box,
  CardActionArea,
  Skeleton,
  Card,
  Button,
  Breadcrumbs,
} from "@mui/material";
import Rating from "@mui/material/Rating";
import FundingStatusChip from "../common/FundingStatusChip";
import { ProposalCardType } from "../../lib/proposalAPI";
import ProfileIcon from "../common/profileIcon";
import { Link } from "react-router-dom";
import styles from "../../styles/proposal/proposalCard.module.css";
import LinkIcon from "@mui/icons-material/Link";
import LinearWithValueLabel from "../common/LinearProgress";
import { toShortNumber } from "../../lib/util";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

export const ProposalCard: React.FC<ProposalCardType> = (props) => {
  const {
    fund_name,
    proposal_name,
    challenge_name,
    user_name,
    funded_status,
    received_percentage,
    result_votes,
    yes_votes,
    no_votes,
    proposal_description,
    rating,
    fund_id,
    challenge_id,
    proposal_id,
    user_id,
    no_assessments,
    url,
    is_challenge_proposal,
    amount_requested,
    sponsored_by_leftovers,
  } = props;

  return (
    <Card
      elevation={4}
      className={
        funded_status ? styles["cardPaper-goldenC"] : styles["cardPaper-greyC"]
      }
    >
      <CardContent sx={{ paddingBottom: "4px", paddingTop: "10px" }}>
        <Box>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            className={styles.proposalBreadCrumb}
          >
            [
            <Link
              to={"/fund/" + fund_id + "/challenges"}
              style={{ width: "25%" }}
            >
              <Typography variant="body1" color="text.primary">
                {fund_name}
              </Typography>
            </Link>
            ,
            <Link to={"/challenge/" + challenge_id} style={{ width: "75%" }}>
              <Typography className={styles.singleLinBreadcrumb}>
                {challenge_name}
              </Typography>
            </Link>
            ]
          </Breadcrumbs>
        </Box>
      </CardContent>

      <CardActionArea
        component={Link}
        to={"/proposal/" + proposal_id}
        state={{ received_percentage: received_percentage }}
      >
        <CardHeader
          title={
            <Typography className={styles.headerTitle}>
              {is_challenge_proposal
                ? "Challenge Proposal: " + proposal_name
                : proposal_name}
            </Typography>
          }
          className={styles.cardHeader}
        />
      </CardActionArea>

      <CardContent sx={{ paddingBottom: "4px !important", paddingTop: "4px" }}>
        <Stack
          direction="column"
          justifyContent="space-evenly"
          alignItems="flex-start"
          spacing={1}
        >
          <CardActionArea
            component={Link}
            to={"/proposer/" + user_id}
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "20px",
              justifyContent: "flex-start",
            }}
          >
            <ProfileIcon
              userName={user_name === null ? "Unknown" : user_name.trim()}
            />
            <div>
              <Box className={styles.ratingBox}>
                <Rating
                  defaultValue={rating ? rating : 0}
                  readOnly
                  precision={0.1}
                />
                <Box sx={{ ml: 1 }}>{`${
                  rating ? rating.toFixed(1) : 0
                } (${no_assessments})`}</Box>
              </Box>
            </div>
          </CardActionArea>
          <div>
            <Box className={styles.ratingBox}>
              <Typography sx={{ fontSize: "0.95rem" }} color="text.primary">
                {"Amount Requested: $" + toShortNumber(amount_requested)}
              </Typography>
            </Box>

            <Box className={styles.ratingBox}>
              <Typography sx={{ fontSize: "0.95rem" }} color="text.primary">
                {"Result Votes: " + toShortNumber(result_votes, "comma")}
              </Typography>
            </Box>
          </div>
          <div
            style={{ display: "flex", width: "100%", flexDirection: "column" }}
          >
            <Box className={styles.ratingBox}>
              <Typography variant="body2" color="green" sx={{ mr: 1 }}>
                {"Up Votes: " + toShortNumber(yes_votes, "comma") + " "}
              </Typography>
              |
              <Typography variant="body2" color="#ba000d" sx={{ ml: 1 }}>
                {`  Down Votes:  ${toShortNumber(no_votes, "comma")}`}
              </Typography>
            </Box>

            <LinearWithValueLabel
              value={(yes_votes / (yes_votes + no_votes)) * 100}
              color1="green"
              color2="#ba000d"
            />
          </div>
          {proposal_description && (
            <Typography className={styles.paragraph}>
              Description: {proposal_description}
            </Typography>
          )}

          {!proposal_description && (
            <Typography className={styles.paragraph}>
              Description Not Available
            </Typography>
          )}
          <FundingStatusChip
            isFunded={funded_status}
            percent={received_percentage}
            sponsored_by_leftovers={sponsored_by_leftovers}
          />

          <Button target="_blank" href={url} startIcon={<LinkIcon />}>
            View proposal on ideascale
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const ProposalCardLoading = () => {
  return (
    <Card elevation={4} className={styles.cardPaper}>
      <Skeleton variant="rectangular" className={styles.skeletonCardHead} />
      <CardContent>
        <Skeleton variant="text" className={styles.skeletonPropName} />
        <Skeleton variant="rectangular" className={styles.fullWidthGrid} />
        <Skeleton variant="circular" className={styles.skeletonCircular} />
        <Skeleton variant="rectangular" className={styles.skeletonRating} />
        <Skeleton variant="rectangular" className={styles.skeletonVotes} />
        <Skeleton variant="text" className={styles.skeletonText} />
        <Skeleton variant="rounded" className={styles.skenletonFundingStatus} />
      </CardContent>
    </Card>
  );
};
